import React, { useState, useEffect, Fragment } from "react";
import { Button, SIZE as BSIZE, KIND } from "baseui/button";
import { ButtonGroup } from "baseui/button-group";
import { ARTWORK_SIZES, ListItem, ListItemLabel } from "baseui/list";
import { useStyletron } from "baseui";
import { HeadingLarge } from "baseui/typography";
import { Block } from "baseui/block";
import { styled } from "styletron-react";
import ItemModal from "../item-modal/ItemModalComponent";
import { RESOURCE_TYPES } from "../../constants/AppEnums";

const MAX_TEXT_LENGTH = 100;
const MAX_TITLE_LENGTH = 100; // Adjust as needed

const SpanBlockLarge = styled("span", {
  display: "block",
  fontSize: "20px",
});
const SpanBlockMedium = styled("span", {
  display: "block",
  fontSize: "14px",
});
const SpanBlockSmall = styled("span", {
  display: "block",
  fontSize: "10px",
  color: "#888",
  margin: "15px 0",
});

export default function ItemList({
  item,
  resourceType,
  onSelectClick,
  editable,
  onCreateClick,
  onBuyClick,
  itemIsLoading,
}) {
  const EMPTY_IMAGE_PATH = require("../../../images/empty.jpg");
  const [imageSrc, setImageSrc] = useState(EMPTY_IMAGE_PATH);
  const [css, theme] = useStyletron();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    if (item.thumbnailImageUrl) setImageSrc(item.thumbnailImageUrl);
  }, [item.id, item.thumbnailImageUrl]);

  const truncate = (text, maxLength) =>
    text?.length > maxLength ? text.substring(0, maxLength - 3) + "..." : text;

  return (
    <Fragment>
      <ListItem
        shape="rounded"
        overrides={{
          Root: {
            style: {
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              ":hover": {
                backgroundColor: theme.colors.backgroundSecondary,
                transform: "translateY(-3px)",
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                cursor: "pointer",
              },
            },
          },
          Description: {
            style: ({ $theme }) => {
              return {
                ...$theme.typography.font850,
              };
            },
          },
          Label: {
            component: ({ children }) => (
              <HeadingLarge>{children}</HeadingLarge>
            ),
          },
        }}
        artwork={() => (
          <Block
            onClick={openModal}
            $style={{
              width: "58px",
              height: "58px",
              borderRadius: "10%",
              overflow: "hidden",
              marginRight: "10px",
              boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={imageSrc}
              alt={item.title}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          </Block>
        )}
        artworkSize={ARTWORK_SIZES.LARGE}
        endEnhancer={() =>
          resourceType === RESOURCE_TYPES.PRODUCT ? (
            <div style={{ display: "flex", alignItems: "center" }}>
              <SpanBlockMedium style={{ marginRight: "15px", minWidth: "40px" }}>
                $ {item.price}
              </SpanBlockMedium>
              <Button
                shape="pill"
                size={BSIZE.compact}
                onClick={() => onBuyClick()} // Assuming onSelectClick is repurposed for the buy action
                overrides={{
                  BaseButton: {
                    style: { width: "100%" },
                  },
                }}
                isLoading={itemIsLoading}
                disabled={itemIsLoading}
              >
                Buy
              </Button>
            </div>
          ) : (
            <ButtonGroup size="compact" kind={KIND.primary}>
              <Button
                shape="pill"
                size={BSIZE.mini}
                onClick={() => onCreateClick()}
                overrides={{ BaseButton: { style: { marginRight: "5px" } } }}
              >
                Clone
              </Button>
              <Button
                shape="pill"
                size={BSIZE.mini}
                disabled={!editable}
                onClick={() => onSelectClick()}
              >
                Select
              </Button>
            </ButtonGroup>
          )
        }
      >
        <Block onClick={openModal}>
          <ListItemLabel
            overrides={{
              LabelContent: {
                style: {
                  fontSize: "24px",
                  margin: "10px",
                },
              },
            }}
          >
            <SpanBlockLarge>
              {truncate(item.title, MAX_TITLE_LENGTH)}
            </SpanBlockLarge>
            <SpanBlockMedium>
              {truncate(item.description, MAX_TEXT_LENGTH)}
            </SpanBlockMedium>
            {resourceType !== RESOURCE_TYPES.PRODUCT && (
              <SpanBlockSmall>
                {`Created by: ${item.creator || "deleted"} - ${
                  item.timeSinceCreated
                }`}
              </SpanBlockSmall>
            )}
          </ListItemLabel>
        </Block>
      </ListItem>
      <ItemModal
        item={item}
        resourceType={resourceType}
        imageSrc={imageSrc}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </Fragment>
  );
}
